import { useEffect, useId, useState, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { prefersReducedMotion } from '@/lib/reduced-motion'
import { useTheme } from '@/lib/theme'

/**
 * The HUB bot avatar (board zsy4): the rounded face from
 * `plans/hub-ai-bot-playground.html` as a React component. One instance is
 * self-contained — its gradient/filter ids are per instance, its idle life
 * (blink, glance, float) runs on plain timers + CSS transitions, so several
 * bots can share a page, each with its own colours. Styles live in
 * `web/globals.css` under `.hub-bot`.
 */

export interface HubBotColors {
  face: string
  faceHi: string
  eye: string
  /** Rim stroke, the "thinking" eye and the working glow. Default: HUB yellow. */
  rim?: string
}

export type HubBotPalette = 'dark' | 'light' | 'yellow' | HubBotColors

export type HubBotState =
  'neutral' | 'left' | 'right' | 'up' | 'down' | 'happy' | 'thinking' | 'sleepy' | 'zoom'

export const HUB_BOT_RIM = '#F5C758'

export const HUB_BOT_PALETTES: Record<'dark' | 'light' | 'yellow', HubBotColors> = {
  dark: { face: '#202020', faceHi: '#353535', eye: '#f5f5f5' },
  light: { face: '#ececec', faceHi: '#ffffff', eye: '#181818' },
  yellow: { face: '#e9b83f', faceHi: '#ffd66d', eye: '#181818' },
}

export function resolveHubBotPalette(palette: HubBotPalette): Required<HubBotColors> {
  const colors = typeof palette === 'string' ? HUB_BOT_PALETTES[palette] : palette
  return { rim: HUB_BOT_RIM, ...colors }
}

/** HUB's own bot follows the theme: dark bot on the light theme, light bot on the dark one. */
export function useHubBotPalette(): HubBotPalette {
  const { resolved } = useTheme()
  return resolved === 'dark' ? 'light' : 'dark'
}

/** Blink: closed for this long, every BLINK_MIN..BLINK_MAX ms. */
const BLINK_MS = 130
const BLINK_MIN_MS = 3_000
const BLINK_MAX_MS = 7_000
/** Glance: look aside for GLANCE_HOLD_MIN..MAX, every GLANCE_MIN..MAX ms. */
const GLANCE_MIN_MS = 5_000
const GLANCE_MAX_MS = 12_000
const GLANCE_HOLD_MIN_MS = 800
const GLANCE_HOLD_MAX_MS = 1_500
const GLANCES: HubBotState[] = ['left', 'right', 'up']

const between = (min: number, max: number) => min + Math.random() * (max - min)

export interface HubBotAvatarProps {
  palette?: HubBotPalette
  /** Rendered size in px (the face fills ~90 % of it; glow and shadow overflow). */
  size?: number
  /** HUB is working on something: the rim glows and pulses. */
  working?: boolean
  /** Pin a look. Without it the bot lives its idle life (blink + glances). */
  state?: HubBotState
  /**
   * false = a still picture: no timers, no float, no hover zoom — for the
   * many earlier replies in a thread, where only the latest one lives.
   */
  animate?: boolean
  className?: string
  /** Decorative by default; give it a label to make it an image. */
  'aria-label'?: string
}

export function HubBotAvatar({
  palette = 'dark',
  size = 52,
  working = false,
  state,
  animate = true,
  className,
  'aria-label': ariaLabel,
}: HubBotAvatarProps) {
  // React's ids carry punctuation that `url(#…)` cannot reference.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const ids = {
    face: `hub-bot-face-${uid}`,
    reflection: `hub-bot-reflection-${uid}`,
    shadow: `hub-bot-shadow-${uid}`,
    glow: `hub-bot-glow-${uid}`,
  }
  const colors = resolveHubBotPalette(palette)
  const [blinking, setBlinking] = useState(false)
  const [glance, setGlance] = useState<HubBotState | null>(null)
  const pinned = state !== undefined
  const effective: HubBotState = state ?? glance ?? 'neutral'

  // Idle life. A pinned state turns the glances off; blinking goes on while
  // the eyes are the open kind. Still picture or reduced motion: no timers.
  const blinks = !pinned || state === 'neutral' || GLANCES.includes(state) || state === 'down'
  useEffect(() => {
    if (!animate || prefersReducedMotion()) return
    const timers = new Set<ReturnType<typeof setTimeout>>()
    const after = (ms: number, fn: () => void) => {
      const id = setTimeout(() => {
        timers.delete(id)
        fn()
      }, ms)
      timers.add(id)
    }
    const scheduleBlink = () =>
      after(between(BLINK_MIN_MS, BLINK_MAX_MS), () => {
        setBlinking(true)
        after(BLINK_MS, () => {
          setBlinking(false)
          scheduleBlink()
        })
      })
    const scheduleGlance = () =>
      after(between(GLANCE_MIN_MS, GLANCE_MAX_MS), () => {
        setGlance(GLANCES[Math.floor(Math.random() * GLANCES.length)])
        after(between(GLANCE_HOLD_MIN_MS, GLANCE_HOLD_MAX_MS), () => {
          setGlance(null)
          scheduleGlance()
        })
      })
    if (blinks) scheduleBlink()
    if (!pinned) scheduleGlance()
    return () => {
      for (const id of timers) clearTimeout(id)
      setBlinking(false)
      setGlance(null)
    }
  }, [animate, blinks, pinned])

  const eyes =
    effective === 'happy' || effective === 'sleepy' || effective === 'thinking'
      ? effective
      : 'normal'

  return (
    <svg
      viewBox="70 70 260 260"
      width={size}
      height={size}
      className={cn('hub-bot', className)}
      data-state={effective}
      data-blink={blinking ? '' : undefined}
      data-working={working ? '' : undefined}
      data-static={animate ? undefined : ''}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      style={
        {
          '--hub-bot-face': colors.face,
          '--hub-bot-face-hi': colors.faceHi,
          '--hub-bot-eye': colors.eye,
          '--hub-bot-rim': colors.rim,
        } as CSSProperties
      }
    >
      <defs>
        <radialGradient id={ids.face} cx="32%" cy="18%" r="92%">
          <stop offset="0%" stopColor="var(--hub-bot-face-hi)" />
          <stop offset="58%" stopColor="var(--hub-bot-face)" />
          <stop offset="100%" stopColor="var(--hub-bot-face)" />
        </radialGradient>
        <linearGradient id={ids.reflection} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity=".14" />
          <stop offset="70%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <filter id={ids.shadow} x="-70%" y="-70%" width="240%" height="240%">
          <feDropShadow dx="0" dy="17" stdDeviation="18" floodColor="rgb(0 0 0 / 0.35)" />
        </filter>
        <filter id={ids.glow} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="9" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect
        className="hub-bot-glow"
        x="77"
        y="77"
        width="246"
        height="246"
        rx="91"
        fill="none"
        stroke="var(--hub-bot-rim)"
        strokeWidth="14"
        filter={`url(#${ids.glow})`}
      />

      <g className="hub-bot-float">
        <g className="hub-bot-body" filter={`url(#${ids.shadow})`}>
          <rect
            className="hub-bot-rim"
            x="83"
            y="83"
            width="234"
            height="234"
            rx="84"
            fill={`url(#${ids.face})`}
            stroke="var(--hub-bot-rim)"
            strokeWidth="3"
            style={working ? { filter: `url(#${ids.glow})` } : undefined}
          />
          <rect
            x="89"
            y="89"
            width="222"
            height="222"
            rx="79"
            fill="none"
            stroke="var(--hub-bot-eye)"
            strokeWidth="1"
            opacity=".12"
          />
          <path
            d="M112 116 C150 84 232 88 279 116 C223 100 166 111 124 141 C112 149 101 132 112 116Z"
            fill={`url(#${ids.reflection})`}
          />

          <g className="hub-bot-gaze">
            <g className="hub-bot-lids">
              <g className="hub-bot-eyes" data-eyes="normal" data-visible={eyes === 'normal'}>
                <rect x="142" y="165" width="31" height="70" rx="15.5" fill="var(--hub-bot-eye)" />
                <rect x="227" y="165" width="31" height="70" rx="15.5" fill="var(--hub-bot-eye)" />
              </g>
              <g className="hub-bot-eyes" data-eyes="happy" data-visible={eyes === 'happy'}>
                <path
                  d="M130 207 Q158 171 186 207"
                  fill="none"
                  stroke="var(--hub-bot-eye)"
                  strokeWidth="17"
                  strokeLinecap="round"
                />
                <path
                  d="M214 207 Q242 171 270 207"
                  fill="none"
                  stroke="var(--hub-bot-eye)"
                  strokeWidth="17"
                  strokeLinecap="round"
                />
              </g>
              <g className="hub-bot-eyes" data-eyes="sleepy" data-visible={eyes === 'sleepy'}>
                <rect x="130" y="194" width="56" height="16" rx="8" fill="var(--hub-bot-eye)" />
                <rect x="214" y="194" width="56" height="16" rx="8" fill="var(--hub-bot-eye)" />
              </g>
              <g className="hub-bot-eyes" data-eyes="thinking" data-visible={eyes === 'thinking'}>
                <rect
                  x="140"
                  y="177"
                  width="31"
                  height="65"
                  rx="15.5"
                  fill="var(--hub-bot-eye)"
                  transform="rotate(-7 155 209)"
                />
                <rect
                  x="229"
                  y="158"
                  width="31"
                  height="69"
                  rx="15.5"
                  fill="var(--hub-bot-rim)"
                  transform="rotate(5 244 193)"
                />
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  )
}
